import * as React from 'react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { Typography } from 'components/typography';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import {
  filterMGBCreateCustomers,
  resolveCustomerDisplayName,
  shouldMirrorBillToFromCustomerChange,
} from './utils';

type CustomerSectionFormProps = {
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  tmsID?: number;
  isCreateMode?: boolean;
  // Used so create search can exclude IDs known as EDI (even without the flag on the row).
  customersByExternalID?: Record<string, TMSCustomer>;
  ediBlockedMessage?: string;
  prevCustomerExternalTMSIDRef?: React.MutableRefObject<string | undefined>;
};

export default function CustomerSectionForm({
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  tmsID,
  isCreateMode = false,
  customersByExternalID = {},
  ediBlockedMessage,
  prevCustomerExternalTMSIDRef,
}: CustomerSectionFormProps): React.JSX.Element {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext<NormalizedLoad>();

  const watchedCustomer = watch('customer');
  const watchedCustomerExternalTMSID = watchedCustomer?.externalTMSID;
  const watchedBillTo = watch('billTo');

  const filterCreateCustomers = React.useCallback(
    (list: TMSCustomer[]) =>
      filterMGBCreateCustomers(list, customersByExternalID, {
        retainExternalTMSID: watchedCustomerExternalTMSID,
      }),
    [customersByExternalID, watchedCustomerExternalTMSID]
  );

  const matchedCustomer = customers?.find(
    (c) => c.externalTMSID === watchedCustomerExternalTMSID?.trim()
  );

  // Create mode: auto-mirror bill-to from customer (Bill To section is hidden).
  useEffect(() => {
    if (!isCreateMode || !prevCustomerExternalTMSIDRef) {
      return;
    }

    const customerId = watchedCustomerExternalTMSID?.trim();
    if (!customerId) {
      return;
    }

    const billToId = watchedBillTo?.externalTMSID?.trim();
    const previousCustomerId = prevCustomerExternalTMSIDRef.current;

    const shouldMirror = shouldMirrorBillToFromCustomerChange(
      billToId,
      customerId,
      previousCustomerId
    );

    if (!shouldMirror) {
      if (customerId !== previousCustomerId) {
        prevCustomerExternalTMSIDRef.current = customerId;
      }
      return;
    }

    const source = matchedCustomer ?? watchedCustomer;
    const displayName = resolveCustomerDisplayName(source);

    if (getValues('billTo.name') !== displayName) {
      setValue('billTo.name', displayName, { shouldDirty: false });
    }
    if (getValues('billTo.externalTMSID') !== customerId) {
      setValue('billTo.externalTMSID', customerId, { shouldDirty: false });
    }

    if (customerId !== previousCustomerId) {
      prevCustomerExternalTMSIDRef.current = customerId;
    }
  }, [
    getValues,
    isCreateMode,
    matchedCustomer,
    prevCustomerExternalTMSIDRef,
    setValue,
    watchedBillTo?.externalTMSID,
    watchedBillTo?.name,
    watchedCustomer,
    watchedCustomerExternalTMSID,
  ]);

  // Keep customer.name in sync with the customers list after TMS search selection.
  // Uses only MGB-local state — no changes to shared RHFCustomerSelect / RHFDebounceSelect.
  useEffect(() => {
    const customerId = watchedCustomerExternalTMSID?.trim();
    if (!customerId) {
      return;
    }

    const fromList = customers?.find((c) => c.externalTMSID === customerId);
    if (!fromList) {
      return;
    }

    const displayName = resolveCustomerDisplayName(fromList);
    if (getValues('customer.name') !== displayName) {
      setValue('customer.name', displayName, {
        shouldDirty: isCreateMode,
        shouldValidate: true,
      });
    }
  }, [
    customers,
    getValues,
    isCreateMode,
    setValue,
    watchedCustomerExternalTMSID,
  ]);

  return (
    <div className='w-full'>
      <RHFCustomerSelect<NormalizedLoad>
        required
        name='customer.externalTMSID'
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        tmsID={tmsID}
        placeholder='Search customers by name'
        searchFieldDefault='name'
        disabled={!isCreateMode}
        filterCustomers={isCreateMode ? filterCreateCustomers : undefined}
        preserveSearchedSelection
      />
      {isCreateMode && (
        <Typography variant='caption' textColor='muted' className='mt-2'>
          EDI customers are not shown in the list. Bill To will be
          auto-populated from the customer when the load is created.
        </Typography>
      )}
      {isCreateMode && ediBlockedMessage && (
        <Typography variant='caption' textColor='error' className='mt-2'>
          {ediBlockedMessage}
        </Typography>
      )}
    </div>
  );
}
